import { EXPORT_ROW_CAP, type CollectOutcome, type FetchPageResult } from './collect.ts';

/**
 * Time-windowed walk of `GET /v1/usage/requests` for the request-log export.
 *
 * The gateway lists newest first (`ORDER BY created_at DESC`) over a
 * half-open instant range: `created_at >= from AND created_at < to`.
 * Offset paging one whole month walks deep enough that a cold archive
 * read can fail, so each query stays inside a short window and starts
 * again at offset 0. Windows are fetched newest → oldest and concatenated
 * in that order, which keeps the API's newest-first order.
 *
 * A window whose first page reports `total > WINDOW_SPLIT_TOTAL` is split
 * in half and the probe page is discarded. Recursion stops at one hour:
 * a one-hour window that is still over the split total is paged as-is.
 * The caller aborts (never truncates) when the running total would pass
 * the export cap.
 */

const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** First page above this total is split instead of paged, when the window is longer than an hour. */
export const WINDOW_SPLIT_TOTAL = 4_000;

/** Windows at or under this length are paged even when `total` is over {@link WINDOW_SPLIT_TOTAL}. */
export const WINDOW_MIN_MS = 60 * 60 * 1000;

/** At most this many `/requests` reads are in flight at once. */
export const WINDOW_FETCH_CONCURRENCY = 3;

export interface InstantWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

interface WindowQuery {
  readonly from: string;
  readonly to: string;
  readonly offset: number;
  readonly limit: number;
}

/**
 * Asia/Shanghai calendar-day slices of `[fromMs, toMs)`.
 *
 * Each slice is the intersection of one Shanghai day with the requested
 * range, oldest first. Adjacent slices share an endpoint: the next
 * `from` equals the previous `to`, so a half-open query neither overlaps
 * nor leaves a gap.
 */
export function shanghaiDayWindows(fromMs: number, toMs: number): readonly InstantWindow[] {
  if (!(fromMs < toMs)) return [];
  const windows: InstantWindow[] = [];
  let dayStart = shanghaiDayStartMs(fromMs);
  while (dayStart < toMs) {
    const dayEnd = dayStart + DAY_MS;
    const start = Math.max(dayStart, fromMs);
    const end = Math.min(dayEnd, toMs);
    if (start < end) windows.push({ fromMs: start, toMs: end });
    if (windows.length > 4_000) {
      throw new Error('request export window did not advance');
    }
    dayStart = dayEnd;
  }
  return windows;
}

function shanghaiDayStartMs(ms: number): number {
  const shifted = ms + SHANGHAI_OFFSET_MS;
  const dayStartShifted = Math.floor(shifted / DAY_MS) * DAY_MS;
  return dayStartShifted - SHANGHAI_OFFSET_MS;
}

export async function collectWindowedRows<T extends { readonly requestId: string }>(options: {
  readonly fromIso: string;
  readonly toIso: string;
  readonly pageSize: number;
  readonly cap?: number;
  readonly fetchPage: (query: WindowQuery) => Promise<FetchPageResult<T>>;
  readonly onProgress?: (rows: number) => void;
}): Promise<CollectOutcome<T>> {
  const fromMs = Date.parse(options.fromIso);
  const toMs = Date.parse(options.toIso);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
    throw new Error('request export window is not a timestamp');
  }
  if (fromMs >= toMs) return { status: 'ok', rows: [] };

  const cap = options.cap ?? EXPORT_ROW_CAP;
  const pageSize = Math.max(1, options.pageSize);
  const gate = new FetchGate(WINDOW_FETCH_CONCURRENCY);
  let fetched = 0;
  let capped = false;

  function accept(batch: readonly T[]): boolean {
    if (capped) return false;
    if (fetched + batch.length > cap) {
      capped = true;
      return false;
    }
    fetched += batch.length;
    options.onProgress?.(fetched);
    return true;
  }

  async function guardedFetch(
    startMs: number,
    endMs: number,
    offset: number,
  ): Promise<FetchPageResult<T> | 'cap'> {
    if (capped) return 'cap';
    return gate.use(async () => {
      if (capped) return 'cap';
      return options.fetchPage({
        from: new Date(startMs).toISOString(),
        to: new Date(endMs).toISOString(),
        offset,
        limit: pageSize,
      });
    });
  }

  async function pageWindow(
    startMs: number,
    endMs: number,
    first: FetchPageResult<T>,
  ): Promise<readonly T[] | 'cap'> {
    const rows: T[] = [];
    let total = first.total;
    if (!accept(first.rows)) return 'cap';
    rows.push(...first.rows);
    if (first.rows.length < pageSize) return rows;
    if (total !== null && rows.length >= total) return rows;

    let offset = pageSize;
    const maxPages = Math.ceil(cap / pageSize) + 1;
    for (let page = 1; page < maxPages; page += 1) {
      if (capped) return 'cap';
      const next = await guardedFetch(startMs, endMs, offset);
      if (next === 'cap' || capped) return 'cap';
      if (next.total !== null) total = next.total;
      if (!accept(next.rows)) return 'cap';
      rows.push(...next.rows);
      if (next.rows.length < pageSize) return rows;
      if (total !== null && rows.length >= total) return rows;
      offset += pageSize;
    }
    capped = true;
    return 'cap';
  }

  async function collectSpan(startMs: number, endMs: number): Promise<readonly T[] | 'cap'> {
    if (capped || endMs <= startMs) return capped ? 'cap' : [];
    const first = await guardedFetch(startMs, endMs, 0);
    if (first === 'cap' || capped) return 'cap';
    if (first.total !== null && fetched + first.total > cap) {
      capped = true;
      return 'cap';
    }
    if (shouldSplit(startMs, endMs, first.total)) {
      const mid = startMs + Math.floor((endMs - startMs) / 2);
      if (mid > startMs && mid < endMs) {
        const [newer, older] = await Promise.all([
          collectSpan(mid, endMs),
          collectSpan(startMs, mid),
        ]);
        if (newer === 'cap' || older === 'cap') return 'cap';
        return [...newer, ...older];
      }
    }
    return pageWindow(startMs, endMs, first);
  }

  const days = shanghaiDayWindows(fromMs, toMs);
  const parts = await Promise.all(days.map((day) => collectSpan(day.fromMs, day.toMs)));
  if (capped) return { status: 'cap' };
  const ordered: T[] = [];
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index];
    if (part === undefined || part === 'cap') return { status: 'cap' };
    ordered.push(...part);
  }
  const unique = dedupeRequestIds(ordered);
  if (unique.length !== fetched) options.onProgress?.(unique.length);
  return { status: 'ok', rows: unique };
}

function shouldSplit(startMs: number, endMs: number, total: number | null): boolean {
  if (total === null || total <= WINDOW_SPLIT_TOTAL) return false;
  return endMs - startMs > WINDOW_MIN_MS;
}

/** Keep the first copy. Callers pass newest-first rows, so the newer copy wins. */
function dedupeRequestIds<T extends { readonly requestId: string }>(rows: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    if (seen.has(row.requestId)) continue;
    seen.add(row.requestId);
    out.push(row);
  }
  return out;
}

class FetchGate {
  private active = 0;
  private readonly waiters: Array<() => void> = [];

  constructor(private readonly limit: number) {}

  async use<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  private acquire(): Promise<void> {
    if (this.active < this.limit) {
      this.active += 1;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.waiters.push(() => {
        this.active += 1;
        resolve();
      });
    });
  }

  private release(): void {
    this.active -= 1;
    const next = this.waiters.shift();
    if (next !== undefined) next();
  }
}
